import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  EmojiEvents,
  Groups,
  Verified,
  Timeline,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const About: React.FC = () => {
  const stats = [
    { icon: <EmojiEvents />, number: '15+', label: 'Years Manufacturing' },
    { icon: <Groups />, number: '5,000+', label: 'Bulk Orders Delivered' },
    { icon: <Verified />, number: '50%', label: 'Direct Savings' },
    { icon: <Timeline />, number: 'All India', label: 'Delivery Network' },
  ];

  const skills = [
    { name: 'Quality Craftsmanship', percentage: 98 },
    { name: 'Customer Service', percentage: 96 },
    { name: 'Design Innovation', percentage: 94 },
    { name: 'Delivery Excellence', percentage: 97 },
  ];

  return (
    <Box id="about" sx={{ py: { xs: 4, md: 8 }, backgroundColor: 'background.default' }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Typography
            variant="h2"
            sx={{
              textAlign: 'center',
              mb: 2,
              color: '#8B7355',
              fontWeight: 'bold',
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' },
            }}
          >
            About Ahmmed Sofas - Manufacturing Excellence
          </Typography>
          
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              mb: { xs: 3, md: 6 },
              color: 'text.secondary',
              maxWidth: 900,
              mx: 'auto',
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
              px: { xs: 1, sm: 2 },
            }}
          >
            At Ahmed Sofas, we craft elegant, custom-made sofas that reflect refined taste and superior comfort. 
            Each piece is thoughtfully designed using premium materials and expert craftsmanship. We specialize in creating 
            bespoke sofas tailored to your space, style, and lifestyle. Experience timeless luxury, made just for you.
          </Typography>
        </motion.div>

        {/* Stats Section */}
        <div className="stats-grid">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="stats-card">
                <Avatar
                  sx={{
                    backgroundColor: 'transparent',
                    border: '2px solid #8B7355',
                    width: { xs: 50, sm: 60 },
                    height: { xs: 50, sm: 60 },
                    mx: 'auto',
                    mb: 2,
                  }}
                >
                  {React.cloneElement(stat.icon, { sx: { color: '#8B7355', fontSize: 32 } })}
                </Avatar>
                <Typography variant="h3" sx={{ fontWeight: 'bold', color: '#8B7355', mb: 1, fontSize: { xs: '1.75rem', sm: '2.25rem' } }}>
                  {stat.number}
                </Typography>
                <Typography variant="body1" sx={{ color: '#8B7355', fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                  {stat.label}
                </Typography>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Skills Section */}
        <Grid container spacing={{ xs: 3, sm: 4, md: 6 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Typography
                variant="h4"
                sx={{ mb: { xs: 2, md: 3 }, fontWeight: 'bold', color: '#8B7355', fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}
              >
                Why to Choose Our Manufacturing?
              </Typography>
              
              <Typography
                variant="body1"
                sx={{ mb: { xs: 3, md: 4 }, lineHeight: 1.7, color: 'text.secondary', fontSize: { xs: '0.9rem', sm: '0.95rem', md: '1rem' } }}
              >
                As a direct manufacturer, we eliminate middlemen and pass savings to you. We handle bulk orders for 
                major retailers like IKEA while maintaining the same premium quality for individual buyers. 
                Our elegant customization services ensure every piece matches your vision perfectly.
              </Typography>

              {skills.map((skill, index) => (
                <Box key={index} sx={{ mb: { xs: 2.5, md: 3 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body1" fontWeight="medium" sx={{ color: '#8B7355', fontSize: { xs: '0.85rem', sm: '0.95rem', md: '1rem' } }}>
                      {skill.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#8B7355', fontSize: { xs: '0.75rem', sm: '0.85rem' } }}>
                      {skill.percentage}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={skill.percentage}
                    sx={{
                      height: { xs: 6, sm: 7, md: 8 },
                      borderRadius: 4,
                      backgroundColor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        backgroundColor: '#8B7355',
                      },
                    }}
                  />
                </Box>
              ))}
            </motion.div>
          </Grid>

          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <Box
                component="img"
                src="/images/about/showroom.jpg"
                alt="Ahmed Sofas Furniture Showroom"
                sx={{
                  width: '100%',
                  height: { xs: 250, sm: 320, md: 400 },
                  borderRadius: 4,
                  objectFit: 'cover',
                  boxShadow: 3,
                }}
              />
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default About;